import { __ } from "@wordpress/i18n";
import { useContext, useState } from "react";
import SbUtils from "../../../Utils/SbUtils";
import Button from "../../Common/Button";
import Input from "../../Common/Input";
import FeedEditorContext from "../../Context/FeedEditorContext";
import CustomizerEmbedModal from "../../Modals/CustomizerEmbedModal";
import PopupEmbedModal from "../../Modals/PopupEmbedModal";
import ModalContainer from "../ModalContainer";

/**
 * CustomizerHeader Component
 *
 * Reusable header for all customizer screens (feeds, popups, etc.)
 * Can be used with context (default) or with props for flexibility.
 *
 * Props-based usage (for review alerts, etc.):
 * - editorTopLoader: loader state
 * - itemName: string - the name to display/edit
 * - onNameChange: function(newName) - callback when name changes
 * - onSave: function(exit) - callback to save
 * - onBack: function() - callback for back/close button
 * - hasChanges: boolean - whether there are unsaved changes
 * - editorConfirmDialog: confirm dialog state
 * - sbCustomizer: window.sb_customizer reference
 * - editorNotification: notification state
 * - embedData: object - data for embed modal { id, type: 'feed'|'popup' }
 * - hideEmbed: boolean - hide the embed button
 *
 * Publish/Draft workflow (for review alerts):
 * - status: 'active' | 'inactive' - current publish status
 * - onPublish: function() - callback to publish (activate)
 * - onUnpublish: function() - callback to revert to draft (deactivate)
 */
const CustomizerHeader = ( props ) => {
    // Always call useContext (React hooks rules)
    // Will be null/undefined if not within FeedEditorContext provider
    const contextData = useContext( FeedEditorContext );

    // Determine if using props or context
    // Use props mode when explicit props are provided OR when context is not available
    const useProps = props.itemName !== undefined || props.onSave !== undefined || !contextData;

    // Get values from props or context
    const sbCustomizer = props.sbCustomizer || contextData?.sbCustomizer || window.sb_customizer;
    const editorTopLoader = props.editorTopLoader;
    const editorNotification = props.editorNotification || contextData?.editorNotification;
    const editorConfirmDialog = props.editorConfirmDialog || contextData?.editorConfirmDialog;
    const headerClasses = props.headerClasses || contextData?.headerClasses || { headerCustomClasses: [], setHeaderCustomClasses: () => {} };

    // For context-based usage (feeds)
    const editorActiveViews = contextData?.editorActiveViews;
    const feedDataInitial = contextData?.feedDataInitial;
    const editorFeedData = contextData?.editorFeedData;
    const editorFeedSettings = contextData?.editorFeedSettings;
    const editorFeedStyling = contextData?.editorFeedStyling;

    // Local state for name editing (props mode)
    const [ isEditingName, setIsEditingName ] = useState( false );
    const [ embedModal, setEmbedModal ] = useState( false );

    // Get item name - from props or context
    const getItemName = () => {
        if ( useProps ) {
            return props.itemName || '';
        }
        return editorFeedData?.feedData?.feed_info?.feed_name || '';
    };

    // Check if currently editing name
    const isNameEditing = () => {
        if ( useProps ) {
            return isEditingName;
        }
        return editorActiveViews?.activeViews?.headerInput || false;
    };

    // Toggle name editing
    const toggleNameEditing = () => {
        if ( useProps ) {
            setIsEditingName( !isEditingName );
        } else if ( editorActiveViews ) {
            editorActiveViews.setActiveViews( {
                ...editorActiveViews.activeViews,
                headerInput: !editorActiveViews.activeViews?.headerInput
            } );
        }
    };

    // Handle name change
    const handleNameChange = ( event ) => {
        const newName = event.currentTarget.value;
        if ( useProps && props.onNameChange ) {
            props.onNameChange( newName );
        } else if ( editorFeedData ) {
            editorFeedData.setFeedData( {
                ...editorFeedData.feedData,
                feed_info: {
                    ...editorFeedData.feedData.feed_info,
                    feed_name: newName
                }
            } );
        }
    };

    // Save handler
    const saveUpdateHeader = ( exit = false ) => {
        if ( useProps && props.onSave ) {
            props.onSave( exit );
        } else {
            SbUtils.saveFeedData( editorFeedData, editorFeedStyling, editorFeedSettings, sbCustomizer, editorTopLoader, editorNotification, exit );
        }
    };

    // Check for unsaved changes
    const hasUnsavedChanges = () => {
        if ( useProps ) {
            // Support both function and boolean for hasChanges prop
            if ( typeof props.hasChanges === 'function' ) {
                return props.hasChanges();
            }
            return props.hasChanges || false;
        }
        return JSON.stringify( feedDataInitial?.settings ) !== JSON.stringify( editorFeedSettings?.feedSettings );
    };

    // Back button click handler
    const backButtonClick = () => {
        if ( hasUnsavedChanges() ) {
            const confirmDialogInfo = {
                active: true,
                heading: __( 'Are you Sure?', 'sb-customizer' ),
                description: __( 'Are you sure you want to leave this page, all unsaved settings will be lost, please make sure to save before leaving.', 'sb-customizer' ),
                confirm: {
                    text: __( 'Save and Exit', 'sb-customizer' ),
                    type: 'primary'
                },
                cancel: {
                    text: __( 'Exit without Saving', 'sb-customizer' ),
                    type: 'destructive'
                }
            };

            confirmDialogInfo.confirm.onConfirm = () => {
                saveUpdateHeader( true );
            };

            confirmDialogInfo.cancel.onCancel = () => {
                if ( useProps && props.onBack ) {
                    props.onBack();
                } else {
                    window.location.href = sbCustomizer.builderUrl;
                }
            };

            editorConfirmDialog?.setConfirmDialog( confirmDialogInfo );
        } else {
            if ( useProps && props.onBack ) {
                props.onBack();
            } else {
                window.location.href = sbCustomizer.builderUrl;
            }
        }
    };

    // Handle embed button click
    const handleEmbedClick = () => {
        saveUpdateHeader();
        if ( headerClasses?.setHeaderCustomClasses ) {
            headerClasses.setHeaderCustomClasses( [ ...( headerClasses.headerCustomClasses || [] ), 'sb-header-zindex' ] );
        }
        setEmbedModal( true );
    };

    // Close embed modal
    const closeEmbedModal = () => {
        setEmbedModal( false );
        if ( headerClasses?.setHeaderCustomClasses ) {
            const cHeaderC = [ ...( headerClasses.headerCustomClasses || [] ) ];
            const indexOfHeader = cHeaderC.indexOf( 'sb-header-zindex' );
            if ( indexOfHeader > -1 ) {
                cHeaderC.splice( indexOfHeader, 1 );
            }
            headerClasses.setHeaderCustomClasses( [ ...cHeaderC ] );
        }
    };

    // Determine embed modal type
    const embedType = props.embedData?.type || 'feed';
    const showEmbed = props.hideEmbed !== true;

    const itemName = getItemName();

    return (
        <>
            <Button
                type='secondary'
                size='medium'
                icon='close'
                text={ __( 'Close Editor', 'sb-customizer' ) }
                customClass='sb-close-btn'
                onClick={ backButtonClick }
            />

            <div className='sb-header-feedname-ctn' data-edit={ isNameEditing() }>
                { isNameEditing() && (
                    <Input
                        type='text'
                        size='medium'
                        customClass='sb-header-feedname-input'
                        value={ itemName }
                        onChange={ handleNameChange }
                        style={ { width: ( ( itemName.length + 2 ) * 7 ) + 'px' } }
                    />
                ) }
                { !isNameEditing() && (
                    <span className="sb-bold sb-standard-p">{ itemName }</span>
                ) }
                <Button
                    type='secondary'
                    size='small'
                    icon={ isNameEditing() ? 'success' : 'pen' }
                    boxshadow={ false }
                    customClass='sb-header-edit-btn'
                    onClick={ toggleNameEditing }
                />
            </div>

            <div className='sb-header-action-btns'>
                <Button
                    type='secondary'
                    size='medium'
                    icon='help'
                    text={ __( 'Help', 'sb-customizer' ) }
                    link='?page=sbr-support'
                    target='_self'
                />
                { showEmbed && (
                    <Button
                        type='secondary'
                        size='medium'
                        icon='code'
                        text={ __( 'Embed', 'sb-customizer' ) }
                        onClick={ handleEmbedClick }
                    />
                ) }
                { /* Publish/Draft workflow for popups */ }
                { props.status !== undefined ? (
                    props.status === 'inactive' ? (
                        <>
                            { /* Draft state: Save Draft + Publish */ }
                            <Button
                                type='secondary'
                                size='medium'
                                icon='success'
                                iconPosition='left'
                                onClick={ () => saveUpdateHeader() }
                                text={ __( 'Save Draft', 'sb-customizer' ) }
                            />
                            <Button
                                type='primary'
                                size='medium'
                                icon='arrow-top'
                                iconPosition='left'
                                onClick={ props.onPublish }
                                text={ __( 'Publish', 'sb-customizer' ) }
                            />
                        </>
                    ) : (
                        <>
                            { /* Published state: Revert to Draft + Save */ }
                            <Button
                                type='secondary'
                                size='medium'
                                icon='reset'
                                iconPosition='left'
                                onClick={ props.onUnpublish }
                                text={ __( 'Revert to Draft', 'sb-customizer' ) }
                            />
                            <Button
                                type='primary'
                                size='medium'
                                icon='success'
                                iconPosition='left'
                                onClick={ () => saveUpdateHeader() }
                                text={ __( 'Save', 'sb-customizer' ) }
                            />
                        </>
                    )
                ) : (
                    <Button
                        type='primary'
                        size='medium'
                        icon='success'
                        iconPosition='left'
                        onClick={ () => saveUpdateHeader() }
                        text={ __( 'Save', 'sb-customizer' ) }
                    />
                ) }
            </div>

            { embedModal && (
                <ModalContainer
                    size='small'
                    closebutton={ true }
                    onClose={ closeEmbedModal }
                >
                    { embedType === 'popup' ? (
                        <PopupEmbedModal
                            popupId={ props.embedData?.id }
                            popupName={ itemName }
                            editorNotification={ editorNotification }
                            sbCustomizer={ sbCustomizer }
                            onClose={ closeEmbedModal }
                        />
                    ) : (
                        <CustomizerEmbedModal
                            editorFeedData={ editorFeedData }
                            editorNotification={ editorNotification }
                            sbCustomizer={ sbCustomizer }
                        />
                    ) }
                </ModalContainer>
            ) }
        </>
    );
};

export default CustomizerHeader;